import React, { useState, useEffect } from 'react';
import { apiService, Modifier } from '../services/api';
import logger from '../utils/logger';

const ModifierManager: React.FC = () => {
  const [modifiers, setModifiers] = useState<Modifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newModifier, setNewModifier] = useState({ name: '' });
  const [editModifier, setEditModifier] = useState({ name: '' });

  useEffect(() => {
    loadModifiers();
  }, []);

  const loadModifiers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getModifiers();
      setModifiers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load modifiers');
      logger.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newModifier.name.trim()) return;

    try {
      await apiService.createModifier({
        name: newModifier.name.trim()
      });
      setNewModifier({ name: '' });
      await loadModifiers();
      setError(null); // Clear any previous errors on success
    } catch (err: any) {
      logger.error('Create error:', err);
      logger.error('Error response details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      // Extract meaningful error message from the response
      let errorMessage = 'Failed to create modifier';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 400) {
          if (err.response.data?.error) {
            errorMessage = err.response.data.error;
            if (err.response.data.details) {
              errorMessage += `: ${err.response.data.details}`;
            }
          } else {
            errorMessage = 'Invalid input data. Please check your modifier name.';
          }
        } else if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editModifier.name.trim()) return;

    try {
      await apiService.updateModifier(id, {
        name: editModifier.name.trim()
      });
      setEditingId(null);
      await loadModifiers();
      setError(null); // Clear any previous errors on success
    } catch (err: any) {
      logger.error('Update error:', err);
      
      // Extract meaningful error message from the response
      let errorMessage = 'Failed to update modifier';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 400) {
          if (err.response.data?.error) {
            errorMessage = err.response.data.error;
            if (err.response.data.details) {
              errorMessage += `: ${err.response.data.details}`;
            }
          } else {
            errorMessage = 'Invalid input data. Please check your modifier name.';
          }
        } else if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.status === 404) {
          errorMessage = 'Modifier not found. It may have been deleted.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this modifier?')) return;

    try {
      await apiService.deleteModifier(id);
      await loadModifiers();
    } catch (err) {
      setError('Failed to delete modifier');
      logger.error('Delete error:', err);
    }
  };

  const startEdit = (modifier: Modifier) => {
    setEditingId(modifier.id!);
    setEditModifier({
      name: modifier.name
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditModifier({ name: '' });
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null); // Clear any previous errors
      const result = await apiService.uploadModifiersCSV(file);
      
      if ((result.errors && result.errors.length > 0) || (result.duplicates && result.duplicates.length > 0)) {
        // Show partial success with errors and/or duplicates
        let message = `Upload completed:\n\nSuccessfully uploaded: ${result.processedCount} modifiers\nTotal processed: ${result.totalCount || result.processedCount}`;
        
        if (result.duplicates && result.duplicates.length > 0) {
          message += `\n\nDuplicates skipped (${result.duplicates.length}):\n${result.duplicates.join('\n')}`;
        }
        
        if (result.errors && result.errors.length > 0) {
          message += `\n\nErrors (${result.errors.length}):\n${result.errors.join('\n')}`;
        }
        
        alert(message);
      } else {
        alert(`Successfully uploaded ${result.processedCount} modifiers`);
      }
      
      await loadModifiers();
    } catch (err: any) {
      logger.error('Upload error:', err);
      
      // Try to extract meaningful error message from the response
      let errorMessage = 'Failed to upload CSV';
      
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please check your API key configuration.';
        } else if (err.response.status === 403) {
          errorMessage = 'Access forbidden. Insufficient permissions.';
        } else if (err.response.data?.error) {
          errorMessage = err.response.data.error;
          if (err.response.data.details && Array.isArray(err.response.data.details)) {
            errorMessage += '\nDetails:\n' + err.response.data.details.join('\n');
          }
        } else {
          errorMessage = `Server error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error: Unable to connect to server. Please check if the backend is running.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      e.target.value = '';
    }
  };

  const handleCSVDownload = async () => {
    try {
      const blob = await apiService.downloadModifiersCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modifiers.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download CSV');
      logger.error('Download error:', err);
    }
  };

  if (loading) return <div className="p-4">Loading modifiers...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Modifier Management</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* CSV Upload/Download Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-3">CSV Operations</h3>
          <div className="flex gap-4 items-center">
            <div>
              <label htmlFor="csv-upload" className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors">
                Upload CSV
              </label>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="hidden"
              />
            </div>
            <button
              onClick={handleCSVDownload}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
            >
              Download CSV
            </button>
            <span className="text-sm text-gray-600">
              CSV format: name
            </span>
          </div>
        </div>

        {/* Create New Modifier Form */}
        <form onSubmit={handleCreate} className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-3">Add New Modifier</h3>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={newModifier.name}
                onChange={(e) => setNewModifier(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter modifier name"
                required
              />
            </div>
            <button
              type="submit"
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
            >
              Add Modifier
            </button>
          </div>
        </form>
      </div>

      {/* Modifiers List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Modifiers ({modifiers.length})</h3>
        </div>
        
        {modifiers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No modifiers found. Create your first modifier above.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {modifiers.map((modifier) => (
              <div key={modifier.id} className="p-4 hover:bg-gray-50">
                {editingId === modifier.id ? (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editModifier.name}
                        onChange={(e) => setEditModifier(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter modifier name"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdate(modifier.id!)}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {modifier.name}
                        </span>
                        <span className="text-sm text-gray-500">
                          (ID: {modifier.id})
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span>
                          Created: {new Date(modifier.created_at || '').toLocaleString()}
                        </span>
                        {modifier.updated_at && modifier.updated_at !== modifier.created_at && (
                          <span className="ml-4">
                            Updated: {new Date(modifier.updated_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => startEdit(modifier)}
                        className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(modifier.id!)}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModifierManager;